import React, { useMemo, useState } from "react";
import { storage } from "../firebase";
import { ref, deleteObject } from "firebase/storage";
import {
  DocumentTextIcon,
  TrashIcon,
  PlusIcon,
  ArrowTopRightOnSquareIcon,
  SparklesIcon,
} from "@heroicons/react/20/solid";
import DocumentUploader from "./shared/DocumentUploader";
import { showAlert } from "../alertService";

const ACCENT = "#1b4d2e";
const DEFAULT_SUGGESTIONS = ["Press Kit", "Reglamento", "Planos", "Contrato"];

const TEMPLATE_VENUE_ID = "neNHHjLxq22wnKLxf8qT";
const isTemplateDocUrl = (url) =>
  typeof url === "string" && url.includes(TEMPLATE_VENUE_ID);

async function deleteStorageObject(path) {
  try {
    await deleteObject(ref(storage, path));
  } catch (err) {
    if (err?.code !== "storage/object-not-found") {
      console.error("Error deleting storage object:", err);
      showAlert("No se pudo borrar el archivo de Storage; se quita igual del listado.", "warning");
    }
  }
}

function genId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `doc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function deriveStoragePath(url) {
  if (!url || typeof url !== "string") return null;
  try {
    const u = new URL(url);
    const m = u.pathname.match(/\/o\/([^?]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

// Traducción conocida de un nombre de documento: los nombres estándar viven en
// el catálogo con su EN, así que si el owner escribe "Reglamento" le podemos
// prellenar "Venue Rules and Regulations" y no tiene que pensarlo.
function catalogEnFor(label, catalog) {
  const key = (label || "").trim().toLowerCase();
  if (!key || !Array.isArray(catalog)) return "";
  const hit = catalog.find((c) => (c?.ES || "").trim().toLowerCase() === key);
  return hit?.EN || "";
}

function normalizeIncoming(values, catalog) {
  if (Array.isArray(values)) {
    return values
      .filter((v) => !isTemplateDocUrl(v?.url))
      .map((v) => ({
        id: v.id || genId(),
        label: v.label || "",
        // Si el nombre es uno del catálogo ("Reglamento", "Contrato"...), la
        // landing EN ya lo traduce sola: se muestra prellenado en vez de vacío
        // para que el editor no diga que falta algo que no falta. Se materializa
        // en Firestore en cuanto el owner toque cualquier cosa de la lista.
        labelEn: v.labelEn || catalogEnFor(v.label, catalog),
        url: v.url || "",
        filename: v.filename || "",
        storagePath: v.storagePath || deriveStoragePath(v.url) || "",
        uploadedAt: v.uploadedAt || null,
        uploadedBy: v.uploadedBy || null,
      }));
  }
  if (values && typeof values === "object") {
    return Object.entries(values)
      .filter(([, url]) => !!url && !isTemplateDocUrl(url))
      .map(([catalogKey, url]) => {
        const catItem = catalog?.find((c) => String(c.id) === String(catalogKey));
        return {
          id: genId(),
          label: catItem?.ES || catItem?.key || `Documento ${catalogKey}`,
          // Los legacy vienen del catálogo, así que su EN ya existe.
          labelEn: catItem?.EN || "",
          url,
          filename: "",
          storagePath: deriveStoragePath(url) || "",
          uploadedAt: null,
          uploadedBy: null,
        };
      });
  }
  return [];
}

const labelInputClass =
  "w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]";

const iconButtonClass =
  "inline-flex h-9 w-9 items-center justify-center rounded-lg text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700 transition-colors";

const ghostButtonClass =
  "inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 transition-colors";

export default function FreeformDocumentsField({
  values,
  onChange,
  venueId,
  catalog,
  currentUser,
}) {
  const entries = useMemo(() => normalizeIncoming(values, catalog), [values, catalog]);
  const [drafts, setDrafts] = useState([]);

  const storagePrefix = venueId ? `venues/${venueId}/documents` : null;

  const updateEntries = (next) => onChange?.(next);

  // Al escribir el nombre ES, si coincide con uno del catálogo y el campo EN
  // sigue vacío, se prellena solo. Nunca pisa algo que el owner ya escribió.
  const handleLabelChange = (id, label) => {
    updateEntries(
      entries.map((e) =>
        e.id === id
          ? { ...e, label, labelEn: e.labelEn || catalogEnFor(label, catalog) }
          : e,
      ),
    );
  };

  const handleLabelEnChange = (id, labelEn) => {
    updateEntries(entries.map((e) => (e.id === id ? { ...e, labelEn } : e)));
  };

  // lang: "es" | "en" — decide a qué slot de archivo va el upload. El slot ES
  // usa los campos históricos (url/filename/storagePath) para no romper los
  // documentos que ya existen; el EN vive en los campos *En.
  const handleReplaced = (id, payload, lang = "es") => {
    const fileFields =
      lang === "en"
        ? { urlEn: payload.url, filenameEn: payload.filename, storagePathEn: payload.storagePath }
        : { url: payload.url, filename: payload.filename, storagePath: payload.storagePath };
    const next = entries.map((e) =>
      e.id === id
        ? {
            ...e,
            ...fileFields,
            uploadedAt: new Date().toISOString(),
            uploadedBy: currentUser?.uid || null,
          }
        : e,
    );
    updateEntries(next);
  };

  const handleRemoveFile = async (id, lang) => {
    const entry = entries.find((e) => e.id === id);
    if (!entry) return;
    const path = lang === "en" ? entry.storagePathEn : entry.storagePath;
    const which = lang === "en" ? "en inglés" : "en español";
    if (!window.confirm(`¿Quitar el archivo ${which} de "${entry.label || "este documento"}"?`)) return;
    if (path) await deleteStorageObject(path);
    const cleared =
      lang === "en"
        ? { urlEn: "", filenameEn: "", storagePathEn: "" }
        : { url: "", filename: "", storagePath: "" };
    updateEntries(entries.map((e) => (e.id === id ? { ...e, ...cleared } : e)));
  };

  const handleSuggestionUpload = (label, payload) => {
    updateEntries([
      ...entries,
      {
        id: genId(),
        label,
        labelEn: catalogEnFor(label, catalog),
        url: payload.url,
        filename: payload.filename,
        storagePath: payload.storagePath,
        urlEn: "",
        filenameEn: "",
        storagePathEn: "",
        uploadedAt: new Date().toISOString(),
        uploadedBy: currentUser?.uid || null,
      },
    ]);
  };

  const handleDraftUpload = (draftId, draft, payload) => {
    updateEntries([
      ...entries,
      {
        id: genId(),
        label: draft.label.trim() || "Documento",
        labelEn: draft.labelEn.trim(),
        url: payload.url,
        filename: payload.filename,
        storagePath: payload.storagePath,
        urlEn: "",
        filenameEn: "",
        storagePathEn: "",
        uploadedAt: new Date().toISOString(),
        uploadedBy: currentUser?.uid || null,
      },
    ]);
    setDrafts((d) => d.filter((x) => x.id !== draftId));
  };

  const handleDelete = async (id) => {
    const entry = entries.find((e) => e.id === id);
    if (!entry) return;
    if (!window.confirm(`¿Borrar "${entry.label || "este documento"}"?`)) return;
    // Borra ambos archivos: un documento puede tener versión ES y EN.
    for (const path of [entry.storagePath, entry.storagePathEn]) {
      if (path) await deleteStorageObject(path);
    }
    updateEntries(entries.filter((e) => e.id !== id));
  };

  const usedLabels = new Set(entries.map((e) => (e.label || "").trim().toLowerCase()));
  const availableSuggestions = DEFAULT_SUGGESTIONS.filter(
    (s) => !usedLabels.has(s.toLowerCase()),
  );

  return (
    <div className="space-y-4">
      {entries.length === 0 && drafts.length === 0 && (
        <p className="text-sm text-zinc-500 italic">
          Aún no hay documentos. Sube uno desde las sugerencias o agrega uno nuevo.
        </p>
      )}

      {entries.map((entry) => (
        <DocumentRow
          key={entry.id}
          entry={entry}
          storagePrefix={storagePrefix}
          venueId={venueId}
          onLabelChange={(v) => handleLabelChange(entry.id, v)}
          onLabelEnChange={(v) => handleLabelEnChange(entry.id, v)}
          onReplaced={(p, lang) => handleReplaced(entry.id, p, lang)}
          onRemoveFile={(lang) => handleRemoveFile(entry.id, lang)}
          onDelete={() => handleDelete(entry.id)}
        />
      ))}

      {drafts.map((draft) => (
        <DraftRow
          key={draft.id}
          draft={draft}
          storagePrefix={storagePrefix}
          venueId={venueId}
          onLabelChange={(label) =>
            setDrafts((d) =>
              d.map((x) =>
                x.id === draft.id
                  ? { ...x, label, labelEn: x.labelEn || catalogEnFor(label, catalog) }
                  : x,
              ),
            )
          }
          onLabelEnChange={(labelEn) =>
            setDrafts((d) => d.map((x) => (x.id === draft.id ? { ...x, labelEn } : x)))
          }
          onCancel={() => setDrafts((d) => d.filter((x) => x.id !== draft.id))}
          onUploaded={(payload) => handleDraftUpload(draft.id, draft, payload)}
        />
      ))}

      {availableSuggestions.length > 0 && (
        <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50/60 p-4">
          <div className="mb-3 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-zinc-500">
            <SparklesIcon className="h-3.5 w-3.5" />
            Sugerencias
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {availableSuggestions.map((label) => (
              <div
                key={label}
                className="flex items-center justify-between gap-3 rounded-lg border border-zinc-200 bg-white px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-zinc-900">{label}</div>
                  <div className="text-xs text-zinc-400">Sin archivo cargado</div>
                </div>
                <DocumentUploader
                  storagePathPrefix={storagePrefix}
                  buttonLabel="Subir"
                  disabled={!venueId}
                  onUploaded={(p) => handleSuggestionUpload(label, p)}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setDrafts((d) => [...d, { id: genId(), label: "", labelEn: "" }])}
        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
        style={{ backgroundColor: ACCENT }}
      >
        <PlusIcon className="h-4 w-4" />
        Agregar otro documento
      </button>
    </div>
  );
}

// Un slot de archivo (español o inglés) dentro de una fila de documento.
// `fallbackNote` se muestra en el slot EN vacío para dejar claro que no queda
// roto: la landing en inglés sirve el archivo en español.
function FileSlot({
  title,
  url,
  storagePrefix,
  venueId,
  onUploaded,
  onRemove,
  fallbackNote,
}) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 px-3 py-2">
      <div className="text-xs font-medium text-zinc-500 mb-1.5">{title}</div>
      <div className="flex flex-wrap items-center gap-2">
        {url ? (
          <>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-700 hover:text-[#1b4d2e]"
            >
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
              Ver
            </a>
            <button
              type="button"
              onClick={onRemove}
              className="text-xs font-medium text-zinc-500 hover:text-red-600"
            >
              Quitar
            </button>
          </>
        ) : (
          <span className="text-xs text-zinc-400">{fallbackNote || "Sin archivo"}</span>
        )}
        <DocumentUploader
          storagePathPrefix={storagePrefix}
          buttonLabel="Subir"
          changingLabel="Cambiar"
          hasExisting={!!url}
          disabled={!venueId}
          onUploaded={onUploaded}
        />
      </div>
    </div>
  );
}

function DocumentRow({
  entry,
  storagePrefix,
  venueId,
  onLabelChange,
  onLabelEnChange,
  onReplaced,
  onRemoveFile,
  onDelete,
}) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: ACCENT + "1a", color: ACCENT }}
        >
          <DocumentTextIcon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Nombre del documento
              </label>
              <input
                type="text"
                value={entry.label}
                onChange={(e) => onLabelChange(e.target.value)}
                placeholder="Ej. Menú de bodas"
                className={labelInputClass}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Nombre en inglés
              </label>
              <input
                type="text"
                value={entry.labelEn}
                onChange={(e) => onLabelEnChange(e.target.value)}
                placeholder="Ej. Wedding menu"
                className={labelInputClass}
              />
              {!entry.labelEn.trim() && (
                <p className="mt-1 text-xs text-amber-600">
                  Sin este nombre, la página en inglés muestra el nombre en español.
                </p>
              )}
            </div>
          </div>

          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <FileSlot
              title="Archivo en español"
              url={entry.url}
              storagePrefix={storagePrefix}
              venueId={venueId}
              onUploaded={(p) => onReplaced(p, "es")}
              onRemove={() => onRemoveFile("es")}
            />
            <FileSlot
              title="Archivo en inglés"
              url={entry.urlEn}
              storagePrefix={storagePrefix}
              venueId={venueId}
              onUploaded={(p) => onReplaced(p, "en")}
              onRemove={() => onRemoveFile("en")}
              fallbackNote={entry.url ? "Se usa el de español" : "Sin archivo"}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={onDelete}
          className={iconButtonClass + " hover:bg-red-50 hover:text-red-600"}
          aria-label="Borrar documento"
          title="Borrar documento"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// Documento nuevo: ambos nombres son obligatorios para poder subir. Es el
// único punto donde se exige el inglés — los documentos que ya existen se
// pueden seguir guardando sin él (se marcan como "EN pendiente" en VenuesList).
function DraftRow({
  draft,
  storagePrefix,
  venueId,
  onLabelChange,
  onLabelEnChange,
  onCancel,
  onUploaded,
}) {
  const missingEs = !draft.label.trim();
  const missingEn = !draft.labelEn.trim();
  const canUpload = !missingEs && !missingEn && !!venueId;
  return (
    <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50/60 p-4">
      <div className="flex items-start gap-3">
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: ACCENT + "1a", color: ACCENT }}
        >
          <DocumentTextIcon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Nombre del documento
              </label>
              <input
                type="text"
                value={draft.label}
                onChange={(e) => onLabelChange(e.target.value)}
                placeholder="Ej. Carta de bebidas"
                className={labelInputClass}
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Nombre en inglés
              </label>
              <input
                type="text"
                value={draft.labelEn}
                onChange={(e) => onLabelEnChange(e.target.value)}
                placeholder="Ej. Drinks menu"
                className={labelInputClass}
              />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <DocumentUploader
              storagePathPrefix={storagePrefix}
              buttonLabel="Subir documento"
              disabled={!canUpload}
              onUploaded={onUploaded}
            />
            <button type="button" onClick={onCancel} className={ghostButtonClass}>
              Cancelar
            </button>
            {!canUpload && venueId && (
              <span className="text-xs text-zinc-500">
                {missingEs && missingEn
                  ? "Escribe el nombre en español y en inglés para poder subir."
                  : missingEs
                    ? "Falta el nombre en español."
                    : "Falta el nombre en inglés."}
              </span>
            )}
          </div>
          <p className="mt-2 text-xs text-zinc-400">
            Después de subirlo puedes agregar una versión en inglés del archivo.
            Si no la subes, la página en inglés usa este mismo archivo.
          </p>
        </div>
      </div>
    </div>
  );
}
